import { RouteLoadProps, LoadedProps, errRedirect } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { rpx } from 'client/lib/rpx-client';
import { StudentNotifications } from 'client/courses/components/student-notifications';
import { UserMenu } from 'client/courses/components/nav/user-menu';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { CommunityLayout } from './community-layout';
import { CommunityHeader } from './community-header';
import { DiscussionFeed } from './discussion-feed';
import { RightSidebar } from './right-sidebar';
import { WelcomeModal } from './welcome-modal';
import { IntakeModal, IntakeStep } from './intake-modal';
import { IntakeCard } from './intake-card';
import { useState } from 'preact/hooks';
import type { IntakeMode, IntakeResults } from 'shared/aip-intake';

type IntakeState = {
  mode: IntakeMode;
  eligible: boolean;
  results?: IntakeResults;
};

function isIntakePreview() {
  return new URLSearchParams(location.search).get('intake') === 'preview';
}

// The shareable link (/community?intake=start) opens the questions for any
// member, any time, without counting as a pop-up view or a skip.
function isIntakeLink() {
  return new URLSearchParams(location.search).get('intake') === 'start';
}

async function loadIntakeState(): Promise<IntakeState> {
  try {
    return await rpx.aipIntake.getState({ preview: isIntakePreview() || undefined });
  } catch {
    return { mode: 'none', eligible: false };
  }
}

async function load(_route: RouteLoadProps) {
  try {
    const [
      feed,
      { discussions: pinnedDiscussions },
      meetingsData,
      modules,
      categories,
      userRole,
      intake,
    ] = await Promise.all([
      rpx.miraseeCommunity.getCommunityFeed({}),
      rpx.miraseeCommunity.getCommunityFeed({ pinnedOnly: true }),
      rpx.miraseeCommunity.getCommunityMeetings(),
      rpx.miraseeCommunity.getCommunityModules(),
      rpx.miraseeCommunity.getCommunityCategories(),
      rpx.miraseeCommunity.getCommunityUserRole(),
      loadIntakeState(),
    ]);

    const canManage = userRole.level === 'guide' || userRole.level === 'facilitator';

    return {
      feed,
      pinnedDiscussions,
      meetings: meetingsData.meetings,
      rsvpAvatars: meetingsData.rsvpAvatars,
      modules,
      categories,
      canPin: canManage,
      canSchedule: canManage,
      canHide: canManage,
      intake,
    };
  } catch (err: any) {
    // 403 = never joined AI Playground. 402 = membership canceled or expired.
    // Either way there is nothing to show on this page, so send the user to
    // their courses, where the join / rejoin CTA lives.
    if (err?.statusCode === 403 || err?.statusCode === 402) {
      throw errRedirect('/community/courses');
    }
    throw err;
  }
}

function Page({ state }: LoadedProps<typeof load>) {
  const {
    feed,
    pinnedDiscussions,
    meetings,
    rsvpAvatars,
    modules,
    categories,
    canPin,
    canSchedule,
    canHide,
  } = state;
  const [intake, setIntake] = useState<IntakeState>(state.intake);
  const [intakeModal, setIntakeModal] = useState<
    { step: IntakeStep; results?: IntakeResults } | undefined
  >(
    isIntakeLink()
      ? { step: 'q1' }
      : state.intake.mode === 'popup'
        ? { step: 'welcome' }
        : undefined,
  );

  function closeIntake(outcome: { results?: IntakeResults }) {
    setIntakeModal(undefined);
    if (isIntakeLink()) {
      history.replaceState(history.state, '', location.pathname);
    }
    if (outcome.results) {
      setIntake({ ...intake, mode: 'done', results: outcome.results });
      return;
    }
    if (intake.mode === 'popup') {
      setIntake({ ...intake, mode: 'card' });
    }
  }

  useDocumentTitle(['Community']);

  return (
    <CommunityLayout isAipMember>
      {!intake.eligible && !isIntakeLink() && <WelcomeModal />}
      {intakeModal && (
        <IntakeModal
          initialStep={intakeModal.step}
          initialResults={intakeModal.results}
          preview={isIntakePreview()}
          fromLink={isIntakeLink()}
          onClose={closeIntake}
        />
      )}
      <CommunityHeader
        actions={
          <>
            <StudentNotifications course={{ id: AIP_COURSE_ID, title: 'AI Playground' }} />
            <UserMenu />
          </>
        }
      />
      {(intake.mode === 'card' || (intake.mode === 'done' && intake.results)) && (
        <IntakeCard
          done={intake.mode === 'done'}
          onOpen={() =>
            setIntakeModal(
              intake.mode === 'done' && intake.results
                ? { step: 'results', results: intake.results }
                : { step: 'q1' },
            )
          }
        />
      )}
      <div class="flex flex-col lg:flex-row gap-8">
        <DiscussionFeed
          initialFeed={feed}
          pinnedDiscussions={pinnedDiscussions}
          categories={categories}
          canPin={canPin}
          canSchedule={canSchedule}
          canHide={canHide}
        />
        <RightSidebar meetings={meetings} rsvpAvatars={rsvpAvatars} modules={modules} />
      </div>
    </CommunityLayout>
  );
}

export const route = defCoursesRoute({
  load,
  Page,
  authLevel: 'student',
});
